import { NestedHome } from '@habity/types';
import { useEffect, useRef, useState } from 'react';
import { SWRInfiniteResponse } from 'swr/dist/infinite';
import { isDev } from '~/lib/utils';

export type SearchData = {
  count: number;
  data: TODO[][];
  error: TODO | null;
};

interface ObserverProps {
  swr: SWRInfiniteResponse<TODO>;
  endingIndicator?: React.ReactNode;
  isCompleted: boolean | ((swr: SWRInfiniteResponse<TODO>) => boolean);
}

export const useObserver = ({
  swr,
  swr: { setSize, data, mutate, size, isValidating, isLoading },
  isCompleted,
}: ObserverProps) => {
  const observer = useRef<TODO | null>(null);
  const [lastElement, setLastElement] = useState(null);
  const isFinished =
    typeof isCompleted === 'function' ? isCompleted(swr) : isCompleted;

  useEffect(() => {
    const loadPage = async () => {
      if (!data || !data.length) return;

      // Fill last item as a placeholder
      const currentItems = data;
      const lastPage: NestedHome[] = data[data.length - 1];
      const loadingPage = lastPage.map((item) => ({
        ...item,
        id: `${item.id}-loading`,
        loading: true,
      }));
      // Insert the last item as a placeholder in a multidimensional array [0][0]
      currentItems.push(loadingPage);
      mutate(currentItems);
    };
    observer.current = new IntersectionObserver((entries) => {
      const first = entries[0];
      if (first.isIntersecting && !isValidating && !isFinished) {
        if (isDev) console.log('Increment to page', size + 1);
        setSize((size) => size + 1);
        loadPage();
      }
    }, {
      rootMargin: '300%', // 3 rows before reaching the element
    });
    return () => {
      observer.current?.disconnect();
    };
  }, [lastElement, isValidating, isFinished]);

  useEffect(() => {
    const currentElement = lastElement;
    const currentObserver = observer.current;

    if (currentElement) {
      currentObserver.observe(currentElement);
    }

    return () => {
      if (currentElement) {
        currentObserver.unobserve(currentElement);
      }
    };
  }, [lastElement]);

  const hasMore = !isFinished && !isLoading;
  const loadMore = async () => {
    if (!hasMore) return;
    if (isDev) console.log('Loading more items, page:', size + 1);
    await setSize((size) => size + 1);
  };

  return { setLastElement, hasMore, loadMore };
};
